import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import BookingApi from "../../../API/Apis/BookingApi";
import BillingDetailsForm from "../../newWebCheckIn/BillingDetails";
import PrivacyPolicyForm from "../../newWebCheckIn/PrivacyPolicyForm";
import RentalContractFirstDriver from "../../newWebCheckIn/RentalContractFirstDriver";
import UploadDocuments from "../../newWebCheckIn/UploadDocuments";
import DriverDetails from "../../newWebCheckIn/DriverDetails";
import AdditionalDriver from "../../newWebCheckIn/AdditionalDriver";
import WebCheckCompletionPage from "../../newWebCheckIn/WebCheckCompletionPage";
import WebCheckInPopUp from "../../newWebCheckIn/WebCheckInPopUp";
import CardWithLoader from "../../newWebCheckIn/CardWithLoader";
import BookingGetters from "../../../store/onlineBooking/getters";
import i18n from "../../../config/i18n";
import { calculateShareboxProgress } from "./progress";
import ShareboxFooter from "../components/ShareboxFooter";
import ShareboxHeader from "../components/ShareboxHeader";

type Props = {
  reservationId: string;
  reservationCode: string;
  customerId?: string;
  isCustPhysicalPerson?: string;
  isCustIndividualCompany?: string;
  driver1Id?: string;
  driver2Id?: string;
  driver3Id?: string;
  onComplete: (contact: {
    email: string;
    mobile: string;
    rentalId?: string;
  }) => void;
  onExit?: () => void;
};

type StepName =
  | "BillingDetails"
  | "PrivacyPolicyForm"
  | "RentalContractFirstDriver"
  | "UploadDocuments"
  | "DriverDetails"
  | "AdditionalDriver"
  | "WebCheckCompletionPage";

export default function D5ConfirmProfile({
  reservationId,
  reservationCode,
  customerId,
  isCustPhysicalPerson,
  isCustIndividualCompany,
  driver1Id,
  driver2Id,
  driver3Id,
  onComplete,
  onExit,
}: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const isShareboxEnabled =
    !!appSettings?.isShareboxEnabled &&
    window.location.pathname.includes("sharebox");
  const [navStack, setNavStack] = useState<StepName[]>(["BillingDetails"]);
  const [isWideStep, setIsWideStep] = useState(false);
  const [webCheckinConfirmed, setWebCheckinConfirmed] = useState<
    boolean | null
  >(null);
  const goToPage = (nextPage: StepName) =>
    setNavStack((prev) => [...prev, nextPage]);
  const goBack = () => {
    if (navStack.length > 1) {
      setNavStack((prev) => prev.slice(0, -1));
    } else {
      onExit?.();
    }
  };
  const page = navStack[navStack.length - 1];
  useEffect(() => {
    if (page !== "DriverDetails" && page !== "BillingDetails")
      setIsWideStep(false);
  }, [page]);

  const [currentDriver, setCurrentDriver] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showDocumentInput, setShowDocumentInput] = useState("");
  const [isLoading, setIsLoading] = useState(!!customerId);
  const [docStep, setDocStep] = useState(1);
  const [initialPrimaryDriverUpload, setInitialPrimaryDriverUpload] =
    useState(true);

  const [currentCustomerDetails, setCurrentCustomerDetails] = useState<any>({});
  const [reservationDetails, setReservationDetails] = useState<any>({
    reservationId,
    reservationCode,
    customerId,
    isCustPhysicalPerson,
    isCustIndividualCompany,
  });
  const [allPrivacyPolicy, setAllPrivacyPolicy] = useState<any[]>([]);
  const [selectedPrivacyPolicy, setSelectedPrivacyPolicy] = useState<string[]>(
    []
  );
  const [documentTypes] = useState<any[]>([]);
  const [allDriverDetails, setAllDriverDetails] = useState<any[]>([]);
  const allDriverCount = allDriverDetails.length;

  const locationLang = i18n.language;
  const textResourceDetails =
    BookingGetters.getTextResourceDetailsBasedOnLang();
  const textResourceData =
    textResourceDetails?.[locationLang]?.flatMap(
      (item: any) => item.data || []
    ) || [];
  const lowQualityUploadedImage =
    textResourceData.find((item: any) => item.key === "lowQualityUploadedImage")
      ?.body || "Low quality image, please retake the photo.";

  useEffect(() => {
    BookingApi.getPrivacyPolicy(reservationId as any, "booking").then(
      (response: any) => {
        const fetchedPolicies = (response.data.privacyPolicy || []).map(
          (item: any) => item.policyGroup
        );
        setAllPrivacyPolicy(fetchedPolicies);
        setSelectedPrivacyPolicy(response.data.selectedPolicies || []);
      }
    );
  }, [reservationId]);

  useEffect(() => {
    if (!customerId) return;
    BookingApi.getCustomerDetails(Number(customerId))
      .then((response: any) => {
        const fetched = response?.data || {};
        setCurrentCustomerDetails({
          ...fetched,
          customerType: fetched.customerType
            ? String(fetched.customerType).toUpperCase()
            : undefined,
        });
      })
      .catch((error: any) => {
        console.error("Failed to fetch the linked customer details:", error);
      })
      .finally(() => setIsLoading(false));
  }, [customerId]);

  const progress = calculateShareboxProgress({
    page,
    currentSection: 1,
    totalSections: 1,
    currentDriver,
  });

  function setDriverDetailsAndInitialDriverIndexAtIndex(
    driverDetails: any,
    index: number
  ) {
    setAllDriverDetails((prev: any[]) => {
      const updated = [...prev];
      updated[index] = { ...driverDetails };
      return updated;
    });
  }

  // Pre-populate any driver(s) already linked to the reservation — same
  // idea as the customer prefetch above, mirroring newWebCheckIn/index.tsx's
  // per-driver getCustomerDetails calls. If driver 1 is already properly
  // registered (isDriver === true), the "customer becomes driver 1" upload
  // cycle should be skipped since there's nothing left to capture for them.
  useEffect(() => {
    if (driver1Id) {
      BookingApi.getCustomerDetails(Number(driver1Id))
        .then((response: any) => {
          const fetched = response?.data || {};
          setDriverDetailsAndInitialDriverIndexAtIndex({ ...fetched }, 0);
          if (fetched.isDriver === true) {
            setInitialPrimaryDriverUpload(false);
          }
        })
        .catch((error: any) => {
          console.error("Failed to fetch driver 1 details:", error);
        });
    }
    if (driver2Id) {
      BookingApi.getCustomerDetails(Number(driver2Id))
        .then((response: any) => {
          setDriverDetailsAndInitialDriverIndexAtIndex(
            { ...(response?.data || {}) },
            1
          );
        })
        .catch((error: any) => {
          console.error("Failed to fetch driver 2 details:", error);
        });
    }
    if (driver3Id) {
      BookingApi.getCustomerDetails(Number(driver3Id))
        .then((response: any) => {
          setDriverDetailsAndInitialDriverIndexAtIndex(
            { ...(response?.data || {}) },
            2
          );
        })
        .catch((error: any) => {
          console.error("Failed to fetch driver 3 details:", error);
        });
    }
  }, [driver1Id, driver2Id, driver3Id]);

  const handleNextPrivacyPolicy = (ids: string[]) => {
    setSelectedPrivacyPolicy(ids);
    if (currentCustomerDetails?.customerType === "COMPANY") {
      setShowDocumentInput("no");
      goToPage("UploadDocuments");
    } else {
      goToPage("RentalContractFirstDriver");
    }
  };

  const handleNextFromDocs = async (data?: any) => {
    if (showDocumentInput === "yes" && initialPrimaryDriverUpload) {
      if (docStep < 3) {
        setDocStep(docStep + 1);
      } else {
        const setDriverResponse =
          await BookingApi.reservationSetCustomerAsDriver1(
            reservationDetails.reservationId
          );
        const driver1Id =
          (setDriverResponse as any)?.data?.driver1Id ||
          (setDriverResponse as any)?.driver1Id;
        if (driver1Id) {
          const refreshed = await BookingApi.getCustomerDetails(driver1Id);
          setDriverDetailsAndInitialDriverIndexAtIndex(
            { ...refreshed.data },
            0
          );
        }
        setInitialPrimaryDriverUpload(false);
        goToPage("AdditionalDriver");
      }
    } else {
      const { driverLicenseFront, driverLicenseBack, idFront, idBack } =
        data?.files || {};
      const hasAtLeastOneFile =
        driverLicenseFront || driverLicenseBack || idFront || idBack;
      if (data?.extractedData && hasAtLeastOneFile) {
        const updated = [...allDriverDetails];
        updated[currentDriver] = {
          ...updated[currentDriver],
          ...data.extractedData,
          identificationDocuments: [idFront, idBack].filter(Boolean),
          drivingLicenseDocuments: [
            driverLicenseFront,
            driverLicenseBack,
          ].filter(Boolean),
        };
        setAllDriverDetails(updated);
      }
      goToPage("DriverDetails");
    }
  };

  const handleBackUploadDocuments = () => {
    if (docStep > 1) {
      setDocStep(docStep - 1);
    } else {
      goBack();
    }
  };

  const handleDriverDetailsNext = (updatedDrivers: any[]) => {
    setIsLoading(false);
    setAllDriverDetails(updatedDrivers);
    setReservationDetails((prev: any) => ({
      ...prev,
      driver1Id: updatedDrivers[0]?.customerId || prev.driver1Id,
      driver2Id: updatedDrivers[1]?.customerId || prev.driver2Id,
      driver3Id: updatedDrivers[2]?.customerId || prev.driver3Id,
    }));
    goToPage("AdditionalDriver");
  };

  const handleComplete = async () => {
    setIsLoading(true);
    let rentalId: string | undefined;
    try {
      const response = await BookingApi.convertReservationToRA(reservationId);
      rentalId = response?.data?.data?.rentalId
        ? String(response.data.data.rentalId)
        : undefined;
    } catch (error: any) {
      console.error("Failed to convert reservation to rental:", {
        reservationId,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
    } finally {
      setIsLoading(false);
    }
    const driver1 = allDriverDetails[0];
    const contactSource = driver1?.email ? driver1 : currentCustomerDetails;
    onComplete({
      email: contactSource?.email || "",
      mobile: contactSource?.telephone || contactSource?.phNum1 || "",
      rentalId,
    });
  };

  let content: React.ReactNode;

  if (page === "BillingDetails" && isLoading) {
    content = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (page === "BillingDetails") {
    content = (
      <BillingDetailsForm
        details={currentCustomerDetails}
        onBack={goBack}
        onNext={(updatedDetails: any) => {
          setCurrentCustomerDetails(updatedDetails);
          if (
            updatedDetails.customerId &&
            updatedDetails.customerId !== reservationDetails.customerId
          ) {
            setReservationDetails((prev: any) => ({
              ...prev,
              customerId: updatedDetails.customerId,
            }));
          }
          goToPage("PrivacyPolicyForm");
        }}
        isSmallScreen={false}
        reservationDetails={reservationDetails}
        progressCalculator={(currentSection, totalSections) =>
          calculateShareboxProgress({
            page: "BillingDetails",
            currentSection,
            totalSections,
            currentDriver: 0,
          })
        }
        isShareboxEnabled={isShareboxEnabled}
        onWideChange={setIsWideStep}
      />
    );
  } else if (page === "PrivacyPolicyForm") {
    content = (
      <Box sx={{ fontFamily: "Inter, sans-serif" }}>
        <PrivacyPolicyForm
          details={allPrivacyPolicy}
          reservationDetails={reservationDetails}
          selectedPrivacyPolicy={selectedPrivacyPolicy}
          onBack={goBack}
          onNext={handleNextPrivacyPolicy}
          isSmallScreen={false}
          progress={progress}
          isShareboxEnabled={isShareboxEnabled}
        />
      </Box>
    );
  } else if (page === "RentalContractFirstDriver") {
    content = (
      <Box sx={{ fontFamily: "Inter, sans-serif" }}>
        <RentalContractFirstDriver
          details={reservationDetails}
          onBack={goBack}
          answer={showDocumentInput}
          onNext={(answer: string) => {
            setShowDocumentInput(answer);
            goToPage("UploadDocuments");
          }}
          progress={progress}
        />
      </Box>
    );
  } else if (page === "UploadDocuments") {
    content = (
      <UploadDocuments
        section={docStep}
        onNext={handleNextFromDocs}
        details={reservationDetails}
        rentalAnswer={showDocumentInput}
        onBack={handleBackUploadDocuments}
        onShowModal={(message: string) => {
          setModalMessage(message);
          setOpenModal(true);
        }}
        documentTypes={documentTypes}
        isSmallScreen={false}
        currentDriver={currentDriver}
        currentCustomerDetails={currentCustomerDetails}
        lowQualityUploadedImage={lowQualityUploadedImage}
        savedFiles={allDriverDetails[currentDriver]}
        progressCalculator={(currentSection, totalSections) =>
          calculateShareboxProgress({
            page: "UploadDocuments",
            currentSection,
            totalSections,
            currentDriver,
          })
        }
        isShareboxEnabled={isShareboxEnabled}
      />
    );
  } else if (page === "DriverDetails") {
    content = (
      <CardWithLoader isLoading={isLoading}>
        <DriverDetails
          reservationDetails={reservationDetails}
          driverNumber={currentDriver}
          documentTypes={documentTypes}
          currentCustomerDetails={currentCustomerDetails}
          onBack={goBack}
          onNext={handleDriverDetailsNext}
          allDriverDetails={allDriverDetails}
          setIsLoading={setIsLoading}
          isSmallScreen={false}
          rentalAnswer={showDocumentInput}
          progressCalculator={(currentSection, totalSections) =>
            calculateShareboxProgress({
              page: "DriverDetails",
              currentSection,
              totalSections,
              currentDriver,
            })
          }
          isShareboxEnabled={isShareboxEnabled}
          onWideChange={setIsWideStep}
        />
      </CardWithLoader>
    );
  } else if (page === "AdditionalDriver") {
    content = (
      <CardWithLoader isLoading={isLoading}>
        <Box sx={{ fontFamily: "Inter, sans-serif" }}>
          <AdditionalDriver
            details={allDriverDetails}
            onBack={goBack}
            onNext={() => goToPage("WebCheckCompletionPage")}
            onAddDriver={(driverNumber: number) => {
              setCurrentDriver(driverNumber);
              setDocStep(1);
              goToPage("UploadDocuments");
            }}
            allDriverDetails={allDriverDetails}
            allDriverCount={allDriverCount}
            isSmallScreen={false}
            progress={() => progress}
            currentDriver={currentDriver}
            reservationDetails={reservationDetails}
            setIsLoading={setIsLoading}
            isShareboxEnabled={isShareboxEnabled}
          />
        </Box>
      </CardWithLoader>
    );
  } else if (webCheckinConfirmed === false) {
    content = (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          py: 8,
          px: 3,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 56, color: "error.main", mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {translate("Web check-in could not be confirmed")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate("Please try again from the start.")}
        </Typography>
        <Button
          variant="contained"
          className="webCheckInButton"
          onClick={onExit}
        >
          {translate("Retry")}
        </Button>
      </Box>
    );
  } else {
    content = (
      <CardWithLoader isLoading={isLoading}>
        <WebCheckCompletionPage
          reservationDetails={reservationDetails}
          onBack={goBack}
          isSmallScreen={false}
          progress={100}
          onConfirmed={(success) => {
            setWebCheckinConfirmed(success);
            if (success) {
              handleComplete();
            }
          }}
          isShareboxEnabled={isShareboxEnabled}
        />
      </CardWithLoader>
    );
  }

  return (
    <>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={reservationCode}
        maxWidth={isWideStep ? 950 : 556}
      />
      <Box
        className="shareboxWebCheckIn"
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "hidden",
          paddingBottom: "5vh",
        }}
      >
        {content}
      </Box>
      <WebCheckInPopUp
        openModal={openModal}
        modalMessage={modalMessage}
        onRequestClose={() => setOpenModal(false)}
        isSmallScreen={false}
      />
      <ShareboxFooter />
    </>
  );
}
